import { Delete } from '@mui/icons-material';
import { AlertColor, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import strings from '../../../../common/Translation/Translate';
import { FormDialog } from '../../../../components/Dialog/FormDialog';
import Notification from '../../../../components/Notification';
import axiosInstance from '../../../../utils/axios';
import { URLS } from '../../../../utils/constants/urls';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import { routes } from 'utils/constants/routes';
import { regex } from 'utils/validators/Validators';
import { bookingDetailToolbarButtonSx } from '../bookingDetailToolbarSx';

type DeleteButtonProps = {
  id: any;
  /** Render a plain menu row instead of the contained toolbar button. */
  compact?: boolean;
  /** Callback fired alongside open (e.g. to close the parent menu). */
  onTriggerClick?: () => void;
};

const maxLength = 250;

export const DeleteButton: React.FC<DeleteButtonProps> = ({ id, compact, onTriggerClick }) => {
  const navigate = useCustomNavigate();

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Sync guard so a second Save click in the same tick cannot start another PUT. */
  const savingRef = useRef(false);
  const [deletionReason, setDeletionReason] = useState<string>('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const handleSave = async () => {
    if (savingRef.current || isLoading) return;
    if (!deletionReason.trim()) {
      setMessage({
        display: true,
        severity: 'warning',
        message: strings.deleteReasonRequired,
      });
      return;
    }

    if (deletionReason.length > maxLength) {
      setMessage({
        display: true,
        severity: 'warning',
        message: strings.formatString(strings.exceeded_character_limit, maxLength) as string,
      });
      return;
    }

    savingRef.current = true;
    try {
      setIsLoading(true);
      await axiosInstance.put(URLS.deleteBookingLineItem(id), {
        deletionReason: deletionReason,
        status: 'DELETED',
      });

      setMessage({
        display: true,
        severity: 'success',
        message: strings.bookingDeletionSuccess,
      });

      handleClose();
      navigate(routes.bookings);
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.error_description || error.response?.data?.message || error.message,
      });
      handleClose();
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setDeletionReason('');
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {compact ? (
        <MenuItem
          onClick={() => {
            onTriggerClick?.();
            setOpenDialog(true);
          }}
          sx={{ py: 0.75, gap: 1, fontSize: '0.875rem', fontWeight: 500, color: 'error.main' }}>
          <Delete sx={{ fontSize: 18 }} />
          {strings.bookingDetailsDeleteBooking}
        </MenuItem>
      ) : (
        <Button
          size="small"
          onClick={() => setOpenDialog(true)}
          variant="contained"
          color="error"
          sx={{ ...bookingDetailToolbarButtonSx }}
          startIcon={<Delete />}
          aria-label={strings.bookingDetailsDeleteBooking}>
          {strings.bookingDetailsDeleteBooking}
        </Button>
      )}
      <FormDialog
        saveButtonText={strings.bookingDetailsDeleteBookingButton}
        open={openDialog}
        handleClose={handleClose}
        title={strings.bookingDetailsDeleteBooking}
        contentText={strings.bookingDetailsDeletionBookingReason}
        loading={isLoading}
        handleSave={handleSave}
        disabledCondition={!regex.string.test(deletionReason)}>
        <TextField
          required={true}
          maxRows={6}
          sx={{
            width: '100%',
            marginTop: '5px',
            '& .MuiFormHelperText-root': {
              textAlign: 'end',
              m: 0,
            },
          }}
          placeholder={strings.bookingDetailsDeletionPlaceholder}
          minRows={6}
          onChange={e => setDeletionReason(e.target.value)}
          multiline={true}
          value={deletionReason}
          error={deletionReason.length > maxLength || !regex.string.test(deletionReason)}
          helperText={
            <Box display={'flex'} justifyContent={deletionReason.length > maxLength ? 'space-between' : 'flex-end'}>
              {!regex.string.test(deletionReason) ? <Typography fontSize={'12px'}>{strings.validateStringErrMsg}</Typography> : ''}
              {<Typography fontSize={'12px'}>{`${deletionReason?.length}/200`}</Typography>}
            </Box>
          }
        />
        <Typography fontSize="13px">{strings.bookingDeletionNote}</Typography>
      </FormDialog>
    </>
  );
};
